import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SOURCES = [
  { value: "sito", label: "Sito", color: "#0F292F" },
  { value: "airbnb", label: "Airbnb", color: "#FF5A5F" },
  { value: "booking", label: "Booking", color: "#003580" },
  { value: "other", label: "Altro", color: "#D4AF37" },
];
const srcMeta = (v) => SOURCES.find((s) => s.value === v) || SOURCES[3];

const EMPTY = {
  guest_name: "", rating: 5, text: "", apartment_id: "",
  country: "", source: "sito", visible: true, review_date: "",
};

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange && onChange(n)} data-testid={`star-${n}`}
          className={onChange ? "cursor-pointer" : "cursor-default"}>
          <Star size={onChange ? 22 : 14} className={n <= value ? "fill-gold text-gold" : "text-line"} />
        </button>
      ))}
    </div>
  );
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [filterApt, setFilterApt] = useState("");
  const [filterSrc, setFilterSrc] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filterApt) params.apartment_id = filterApt;
    if (filterSrc) params.source = filterSrc;
    api.get("/admin/reviews", { params }).then((r) => setReviews(r.data)).catch(() => {});
  }, [filterApt, filterSrc]);

  useEffect(() => { api.get("/admin/apartments").then((r) => setApartments(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setEditing({ ...editing, [k]: v });

  const save = async () => {
    if (!editing.guest_name || !editing.text) {
      toast.error("Inserisci nome ospite e testo della recensione");
      return;
    }
    setSaving(true);
    const payload = { ...editing, rating: Number(editing.rating) };
    try {
      if (editing.id) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Recensione salvata");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa recensione?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Recensione eliminata");
    load();
  };

  const toggleVisible = async (rv) => {
    await api.put(`/admin/reviews/${rv.id}`, { ...rv, visible: !rv.visible });
    load();
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif font-light text-4xl text-petrol">Recensioni</h1>
          <p className="text-muted-foreground">Aggiungi recensioni manuali o copiate da Airbnb/Booking, marcate per fonte</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={filterApt} onChange={(e) => setFilterApt(e.target.value)} data-testid="reviews-apt-filter"
            className="border border-line rounded-sm px-3 py-2.5 text-sm text-petrol bg-card outline-none">
            <option value="">Tutti gli appartamenti</option>
            {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select value={filterSrc} onChange={(e) => setFilterSrc(e.target.value)} data-testid="reviews-src-filter"
            className="border border-line rounded-sm px-3 py-2.5 text-sm text-petrol bg-card outline-none">
            <option value="">Tutte le fonti</option>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={() => setEditing({ ...EMPTY })} data-testid="add-review" className="flex items-center gap-2 bg-petrol text-white px-4 py-2.5 rounded-sm hover:bg-[#1A3A41] transition-colors text-sm">
            <Plus size={16} /> Recensione
          </button>
        </div>
      </div>

      <div className="bg-card border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-beige text-petrol/70">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Ospite</th>
              <th className="text-left px-4 py-3 font-medium">Voto</th>
              <th className="text-left px-4 py-3 font-medium">Recensione</th>
              <th className="text-left px-4 py-3 font-medium">Appartamento</th>
              <th className="text-left px-4 py-3 font-medium">Fonte</th>
              <th className="text-left px-4 py-3 font-medium">Visibile</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {reviews.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Nessuna recensione. Aggiungine una con il pulsante in alto.</td></tr>}
            {reviews.map((rv) => (
              <tr key={rv.id} className="border-t border-line align-top" data-testid={`review-row-${rv.id}`}>
                <td className="px-4 py-3 text-petrol font-medium whitespace-nowrap">{rv.guest_name}<div className="text-xs text-muted-foreground font-normal">{rv.country}{rv.review_date ? ` · ${rv.review_date}` : ""}</div></td>
                <td className="px-4 py-3"><Stars value={rv.rating} /></td>
                <td className="px-4 py-3 text-muted-foreground max-w-md"><span className="line-clamp-2">{rv.text}</span></td>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{rv.apartment_name || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-full" style={{ background: srcMeta(rv.source).color }} />{srcMeta(rv.source).label}</span>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleVisible(rv)} data-testid={`toggle-visible-${rv.id}`} className={`flex items-center gap-1 text-xs ${rv.visible ? "text-green-700" : "text-muted-foreground"}`}>
                    {rv.visible ? <Eye size={15} /> : <EyeOff size={15} />} {rv.visible ? "Sì" : "No"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEditing({ ...EMPTY, ...rv })} data-testid={`edit-review-${rv.id}`} className="text-petrol hover:text-gold"><Pencil size={15} /></button>
                    <button onClick={() => remove(rv.id)} data-testid={`del-review-${rv.id}`} className="text-destructive hover:opacity-70"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setEditing(null)}>
          <div className="bg-cream rounded-sm w-full max-w-xl my-auto" onClick={(e) => e.stopPropagation()} data-testid="review-editor">
            <div className="flex items-center justify-between p-5 border-b border-line">
              <h2 className="font-serif text-2xl text-petrol">{editing.id ? "Modifica recensione" : "Nuova recensione"}</h2>
              <button onClick={() => setEditing(null)}><X size={20} className="text-muted-foreground" /></button>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Nome ospite *</span>
                <input value={editing.guest_name} onChange={(e) => set("guest_name", e.target.value)} data-testid="review-guest"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Paese</span>
                <input value={editing.country} onChange={(e) => set("country", e.target.value)} data-testid="review-country"
                  placeholder="es. Italia" className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <div className="block">
                <span className="overline text-[10px] text-muted-foreground">Voto</span>
                <div className="mt-2"><Stars value={Number(editing.rating)} onChange={(n) => set("rating", n)} /></div>
              </div>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Data recensione</span>
                <input type="date" value={editing.review_date} onChange={(e) => set("review_date", e.target.value)} data-testid="review-date"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Appartamento</span>
                <select value={editing.apartment_id} onChange={(e) => set("apartment_id", e.target.value)} data-testid="review-apartment"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  <option value="">Generale (nessuno)</option>
                  {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Fonte</span>
                <select value={editing.source} onChange={(e) => set("source", e.target.value)} data-testid="review-source"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="block sm:col-span-2">
                <span className="overline text-[10px] text-muted-foreground">Testo recensione *</span>
                <textarea value={editing.text} onChange={(e) => set("text", e.target.value)} rows={4} data-testid="review-text"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none leading-relaxed" />
              </label>
              <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer">
                <input type="checkbox" checked={!!editing.visible} onChange={(e) => set("visible", e.target.checked)} data-testid="review-visible" className="w-4 h-4 accent-petrol" />
                <span className="text-sm text-petrol">Mostra questa recensione sul sito pubblico</span>
              </label>
            </div>
            <div className="p-5 border-t border-line flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-line text-petrol rounded-sm hover:bg-beige transition-colors">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="save-review" className="px-6 py-2.5 bg-petrol text-white rounded-sm hover:bg-[#1A3A41] transition-colors disabled:opacity-60">
                {saving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
